//all the request to the api will be done here
import axios from 'axios'
import {apiConfig} from '../config/apiconfig'
import {store} from '../../redux/store'
import { jwtDecode } from 'jwt-decode';

// Fonction pour vérifier si le token est valide et non expiré
const isTokenValid = (token) => {
    try {
        if (!token) {
            return false;
        }
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        // Vérifie si le token est expiré
        if (decodedToken.exp < currentTime) {
            return false;
        }
        
        return true;
    } catch (error) {
        console.error('Erreur lors de la vérification du token:', error);
        return false;
    }
};

export const httpClient = axios.create({
    baseURL: apiConfig.baseURL,
    timeout: apiConfig.timeout,
    headers: apiConfig.headers
});

// Intercepteur pour ajouter le token à chaque requête
httpClient.interceptors.request.use(
    (config) => {
        // Check if we need to force a public token
        if (config.usePublicToken) {
            config.headers.Authorization = `Bearer ${process.env.REACT_APP_PUBLIC_API_KEY}`;
            delete config.usePublicToken;
        } else {
            // Normal token lookup: use auth or sign-in token if available,
            // otherwise fallback to the public token.
            const reduxState = store.getState();
            const authToken = reduxState?.auth?.token;
            const signInToken = reduxState?.signIn?.token;

            if (authToken && isTokenValid(authToken)) {
                config.headers.Authorization = `Bearer ${authToken}`;
            } else if (signInToken && isTokenValid(signInToken)) {
                config.headers.Authorization = `Bearer ${signInToken}`;
            } else if (process.env.REACT_APP_PUBLIC_API_KEY) {
                config.headers.Authorization = `Bearer ${process.env.REACT_APP_PUBLIC_API_KEY}`;
            } else {
                // Si aucun token valide n'est trouvé, déconnecter l'utilisateur
                store.dispatch({ type: 'auth/userLogout' });
                return Promise.reject(new Error('Token invalide ou expiré'));
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Intercepteur pour gérer les erreurs de réponse
httpClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response) {
            // Erreur avec réponse du serveur
            if (error.response.status === 401 || error.response.status === 403) {
                // Token expiré ou invalide
                store.dispatch({ type: 'auth/userLogout' });
            }
        }
        return Promise.reject(error);
    }
);

export const getRequest = async (url, params = {}, usePublicToken = false) => {
    try {
        const config = { params };
        if (usePublicToken) {
            config.usePublicToken = true;
        }
        const response = await httpClient.get(url, config);
        return { data: response.data, status: response.status };
    } catch (error) {
        throw error.response || error;
    }
};

export const postRequestJson = async (url, body) => {
    try {
        const response = await httpClient.post(`${process.env.REACT_APP_API_URL}${url}`, body);
        return { data: response.data, status: response.status };
    } catch (error) {
        throw error.response.data.error;
    }
};

export const postRequestFormData = async (url, body, config = {}) => {
    try {
        // Vérification que body est bien un FormData
        if (!(body instanceof FormData)) {
            throw new Error('Le corps de la requête doit être un objet FormData');
        }

        const response = await httpClient.post(`${process.env.REACT_APP_API_URL}${url}`, body, {
            headers: { 
                'Content-Type': 'multipart/form-data',
                ...config.headers 
            },
            ...config,
        });

        return { data: response.data, status: response.status };
    } catch (error) {
        if (error.response) {
            // Erreur avec réponse du serveur
            const errorMessage = error.response.data?.error || error.response.data?.message || 'Une erreur est survenue lors de l\'envoi des fichiers';
            throw new Error(errorMessage);
        } else if (error.request) {
            // Erreur sans réponse du serveur
            throw new Error('Le serveur ne répond pas. Veuillez vérifier votre connexion.');
        } else {
            // Erreur lors de la configuration de la requête
            throw new Error(error.message || 'Une erreur est survenue lors de la préparation de la requête');
        }
    }
}

export const deleteRequest = async (url) => {
    try{
        const response = await httpClient.delete(`${process.env.REACT_APP_API_URL}` + url);
        return {data: response.data, status: response.status};
    }catch (error){
        throw error.response.data.error;
    }
}

export const patchRequest = async (url, body) => {
    try{
        const response = await httpClient.patch(`${process.env.REACT_APP_API_URL}` + url, body);
        return {data: response.data, status: response.status};
    }catch (error){
        throw error.response.data.error;
    }
}

//post request with no token
export const postRequestJsonNotToken = async (url, data) => {
    try{
        const response = await axios.post(`${process.env.REACT_APP_API_URL}` + url, data);
        return {data: response.data, status: response.status};
    }catch (error){
        throw error.response.data.error;
    }
}



